/**
 * Admin Dashboard Page
 *
 * Main dashboard for admin section with stats and quick actions.
 *
 * @fileoverview Admin dashboard page
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Skeleton,
  useColorModeValue,
  Icon,
  SimpleGrid,
  Tooltip,
  IconButton,
} from '@chakra-ui/react';
import {
  MdAdd,
  MdBusiness,
  MdPeople,
  MdAccountBalance,
  MdWarning,
  MdCheckCircle,
  MdTrendingUp,
  MdOpenInNew,
  MdSwitchAccount,
} from 'react-icons/md';
import { useAdminTenantStats, useRecentTenants } from '../../hooks/useAdminTenants';
import { usePlaidHealthBoard } from '../../hooks/usePlaidHealth';
import { useSupportMode } from '../../shared/support-mode';

// ─── StatCard ─────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ElementType;
  iconColor: string;
  loading?: boolean;
  onClick?: () => void;
  alertCount?: number;
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  iconColor,
  loading,
  onClick,
  alertCount,
}) => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <Box
      bg={bgColor}
      border="1px"
      borderColor={borderColor}
      borderRadius="lg"
      p={4}
      cursor={onClick ? 'pointer' : 'default'}
      _hover={onClick ? { shadow: 'md', borderColor: 'blue.300' } : {}}
      onClick={onClick}
      transition="all 0.15s"
    >
      {loading ? (
        <Skeleton h="60px" borderRadius="md" />
      ) : (
        <>
          <HStack justify="space-between" mb={2}>
            <Icon as={icon} color={iconColor} boxSize={5} />
            {alertCount !== undefined && alertCount > 0 && (
              <Badge colorScheme="orange" variant="solid" borderRadius="full" fontSize="xs">
                {alertCount}
              </Badge>
            )}
          </HStack>
          <Text fontSize="2xl" fontWeight="bold">{value}</Text>
          <Text fontSize="xs" color={mutedColor} mt={1}>{label}</Text>
        </>
      )}
    </Box>
  );
};

// ─── SectionLabel ─────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  return (
    <Text
      fontSize="xs"
      fontWeight="semibold"
      color={labelColor}
      textTransform="uppercase"
      letterSpacing="wider"
    >
      {children}
    </Text>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { stats, loading: statsLoading } = useAdminTenantStats();
  const { tenants: recentTenants, loading: tenantsLoading } = useRecentTenants(5);
  const { data: healthData, loading: healthLoading } = usePlaidHealthBoard();
  const { enterSupportMode } = useSupportMode();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const rowHoverBg = useColorModeValue('gray.50', 'gray.700');

  const isLoading = statsLoading || healthLoading;

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const connectionIssues = healthData?.totalUnhealthyTokens ?? 0;

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <HStack justify="space-between" wrap="wrap" gap={4}>
        <Box>
          <Heading size="lg">Admin Dashboard</Heading>
          <Text color={mutedColor} mt={1}>
            Overview of your tenant management system
          </Text>
        </Box>
        <Button
          colorScheme="blue"
          leftIcon={<Icon as={MdAdd} />}
          onClick={() => navigate('/admin/tenants/new')}
        >
          Create Tenant
        </Button>
      </HStack>

      {/* Tenants section */}
      <VStack align="stretch" spacing={2}>
        <SectionLabel>Tenants</SectionLabel>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <StatCard
            label="Total tenants"
            value={stats?.totalTenants ?? '—'}
            icon={MdBusiness}
            iconColor="blue.400"
            loading={isLoading}
          />
          <StatCard
            label="Active"
            value={stats?.activeTenants ?? '—'}
            icon={MdCheckCircle}
            iconColor="green.400"
            loading={isLoading}
          />
          <StatCard
            label="Inactive"
            value={stats?.inactiveTenants ?? '—'}
            icon={MdBusiness}
            iconColor="gray.400"
            loading={isLoading}
          />
          <StatCard
            label="New this month"
            value={stats?.tenantsThisMonth ?? '—'}
            icon={MdTrendingUp}
            iconColor="purple.400"
            loading={isLoading}
          />
        </SimpleGrid>
      </VStack>

      {/* Bank Connections section */}
      <VStack align="stretch" spacing={2}>
        <SectionLabel>Bank Connections</SectionLabel>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <StatCard
            label="Tenants with banks"
            value={stats?.tenantsWithBanks ?? '—'}
            icon={MdBusiness}
            iconColor="teal.400"
            loading={isLoading}
            onClick={() => navigate('/admin/connected-accounts')}
          />
          <StatCard
            label="Banks connected"
            value={stats?.totalConnectedBanks ?? '—'}
            icon={MdAccountBalance}
            iconColor="blue.400"
            loading={isLoading}
            onClick={() => navigate('/admin/connected-accounts')}
          />
          <StatCard
            label="Total accounts"
            value={stats?.totalConnectedAccounts ?? '—'}
            icon={MdAccountBalance}
            iconColor="cyan.500"
            loading={isLoading}
            onClick={() => navigate('/admin/connected-accounts')}
          />
          <StatCard
            label="Total users"
            value={stats?.totalUsers ?? '—'}
            icon={MdPeople}
            iconColor="purple.400"
            loading={isLoading}
          />
        </SimpleGrid>
      </VStack>

      {/* Connection Health section */}
      <VStack align="stretch" spacing={2}>
        <SectionLabel>Connection Health</SectionLabel>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <StatCard
            label="Active banks"
            value={stats?.totalConnectedBanks ?? '—'}
            icon={MdCheckCircle}
            iconColor="green.400"
            loading={isLoading}
            onClick={() => navigate('/admin/connected-accounts')}
          />
          <StatCard
            label="Connection issues"
            value={connectionIssues}
            icon={MdWarning}
            iconColor={connectionIssues > 0 ? 'orange.400' : 'green.400'}
            loading={isLoading}
            onClick={() => navigate('/admin/plaid-health')}
            alertCount={connectionIssues > 0 ? connectionIssues : undefined}
          />
        </SimpleGrid>
      </VStack>

      {/* Recent Tenants */}
      <Box
        bg={bgColor}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        overflow="hidden"
      >
        <Box p={4} borderBottom="1px" borderColor={borderColor}>
          <HStack justify="space-between">
            <HStack>
              <Icon as={MdBusiness} color="blue.500" boxSize={5} />
              <Heading size="sm">Recent Tenants</Heading>
            </HStack>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => navigate('/admin/tenants')}
            >
              View All
            </Button>
          </HStack>
        </Box>

        {tenantsLoading ? (
          <Box p={4}>
            <VStack spacing={3}>
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} h="40px" w="100%" />
              ))}
            </VStack>
          </Box>
        ) : recentTenants.length === 0 ? (
          <Box p={8} textAlign="center">
            <Text color={mutedColor}>No tenants yet</Text>
            <Button
              mt={4}
              colorScheme="blue"
              variant="outline"
              onClick={() => navigate('/admin/tenants/new')}
            >
              Create Your First Tenant
            </Button>
          </Box>
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Slug</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {recentTenants.map((tenant) => (
                <Tr
                  key={tenant.id}
                  cursor="pointer"
                  _hover={{ bg: rowHoverBg }}
                  onClick={() => navigate(`/admin/tenants/${tenant.id}`)}
                >
                  <Td fontWeight="medium">{tenant.name}</Td>
                  <Td>
                    <Text fontSize="sm" color={mutedColor} fontFamily="mono">
                      {tenant.slug}
                    </Text>
                  </Td>
                  <Td>
                    <Badge
                      colorScheme={tenant.isActive ? 'green' : 'red'}
                      variant="subtle"
                    >
                      {tenant.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color={mutedColor}>
                      {formatDate(tenant.createdAt)}
                    </Text>
                  </Td>
                  <Td>
                    <HStack spacing={1} justify="flex-end">
                      <Tooltip label="View tenant's transactions" placement="top" hasArrow>
                        <IconButton
                          aria-label="Enter support mode"
                          icon={<Icon as={MdSwitchAccount} boxSize={5} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="purple"
                          _hover={{
                            bg: 'purple.50',
                            color: 'purple.600',
                            transform: 'scale(1.1)',
                            shadow: 'sm',
                          }}
                          transition="all 0.15s"
                          onClick={(e) => {
                            e.stopPropagation();
                            enterSupportMode({
                              id: tenant.id,
                              name: tenant.name,
                              slug: tenant.slug,
                              managerEmail: tenant.managerEmail,
                              isActive: tenant.isActive,
                              createdAt: tenant.createdAt,
                            });
                            navigate('/home');
                          }}
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>
    </VStack>
  );
};

export default AdminDashboard;
